import React, { useState } from 'react';
import { adminService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Download, FileText, Users, TrendingUp, DollarSign,
  AlertCircle, Loader2, CheckCircle2, Clock, Star,
  BarChart2, RefreshCw, Edit2, Save, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const currentYear  = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

// ── Stat Card ─────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Icon, color = 'forest' }) => (
  <div className="bg-white border border-cream-200 rounded-2xl p-5">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 bg-${color}-50 rounded-xl flex items-center justify-center`}>
        <Icon size={18} className={`text-${color}-600`} />
      </div>
    </div>
    <div className="font-serif text-2xl font-bold text-forest-900">{value ?? '—'}</div>
    <div className="text-xs font-medium text-forest-600 mt-1">{label}</div>
    {sub && <div className="text-xs text-forest-400 mt-0.5">{sub}</div>}
  </div>
);

// ── Expert Rate Modal ─────────────────────────────────────────
const RateModal = ({ expert, existingRate, onSaved, onClose }) => {
  const [form, setForm] = useState({
    sessionRatePerHour: existingRate?.sessionRatePerHour ?? '',
    canvasReviewRate:   existingRate?.canvasReviewRate   ?? '',
    platformCommission: existingRate?.platformCommission ?? 10,
    currency:           existingRate?.currency           ?? 'GHS',
    notes:              existingRate?.notes              ?? '',
  });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.sessionRatePerHour && !form.canvasReviewRate) {
      toast.error('Set at least one rate'); return;
    }
    setSaving(true);
    try {
      await adminService.setExpertRate(expert.id, {
        sessionRatePerHour: Number(form.sessionRatePerHour) || 0,
        canvasReviewRate:   Number(form.canvasReviewRate)   || 0,
        platformCommission: Number(form.platformCommission) || 0,
        currency:           form.currency,
        notes:              form.notes,
      });
      toast.success('Payment rate saved');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save rate');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif text-xl text-forest-900">Set Payment Rate</h3>
            <p className="text-xs text-forest-500 mt-0.5">{expert.name}</p>
          </div>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1">Session Rate / Hour *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-forest-400">{form.currency}</span>
                <input type="number" value={form.sessionRatePerHour} onChange={f('sessionRatePerHour')} min="0" step="0.01"
                  placeholder="0.00" className="w-full pl-12 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1">Canvas Review Rate</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-forest-400">{form.currency}</span>
                <input type="number" value={form.canvasReviewRate} onChange={f('canvasReviewRate')} min="0" step="0.01"
                  placeholder="0.00" className="w-full pl-12 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
              </div>
              <p className="text-[10px] text-forest-400 mt-1">Per submission graded</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1">Platform Commission %</label>
              <input type="number" value={form.platformCommission} onChange={f('platformCommission')} min="0" max="100" step="0.1"
                className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1">Currency</label>
              <select value={form.currency} onChange={f('currency')}
                className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white">
                <option value="GHS">GHS (Ghana Cedi)</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Notes (optional)</label>
            <textarea value={form.notes} onChange={f('notes')} rows={2}
              placeholder="Any notes about this rate agreement..."
              className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
          </div>
        </div>

        <div className="bg-cream-50 border border-cream-200 rounded-xl p-3 text-xs text-forest-600">
          <strong>Preview:</strong> A 1-hour session = {form.currency} {Number(form.sessionRatePerHour || 0).toFixed(2)} gross.
          After {form.platformCommission}% commission = {form.currency} {(Number(form.sessionRatePerHour || 0) * (1 - Number(form.platformCommission || 0) / 100)).toFixed(2)} net to expert.
        </div>

        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving}
            className="btn-primary flex-1 justify-center disabled:opacity-60 flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Rate
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 border border-cream-200 rounded-full text-forest-600 text-sm hover:border-forest-400">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Excel Export ──────────────────────────────────────────────
const exportToExcel = (summary, impact, period) => {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Expert Payment Summary
  const expertRows = [
    ['TOURENT HUB — EXPERT PAYMENT REPORT', '', '', '', '', '', '', ''],
    [`Period: ${period}`, '', '', '', '', '', '', ''],
    [`Generated: ${new Date().toLocaleString('en-GH')}`, '', '', '', '', '', '', ''],
    [],
    ['Expert Name', 'Email', 'Sessions', 'Session Hours', 'Canvas Reviews', 'Gross (GHS)', 'Commission (GHS)', 'Net Payable (GHS)', 'Status'],
    ...summary.map(e => [
      e.expertName,
      e.email,
      e.sessions,
      Number(e.sessionHours).toFixed(2),
      e.canvasReviews,
      Number(e.totalGross).toFixed(2),
      Number(e.totalCommission).toFixed(2),
      Number(e.totalNet).toFixed(2),
      e.status,
    ]),
    [],
    ['TOTALS', '', summary.reduce((s, e) => s + e.sessions, 0), '', summary.reduce((s, e) => s + e.canvasReviews, 0),
      `=SUM(F6:F${5 + summary.length})`, `=SUM(G6:G${5 + summary.length})`, `=SUM(H6:H${5 + summary.length})`, ''],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(expertRows);
  ws1['!cols'] = [{ wch: 25 }, { wch: 30 }, { wch: 10 }, { wch: 14 }, { wch: 15 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Expert Payments');

  // Sheet 2: Platform Impact
  if (impact) {
    const impactRows = [
      ['TOURENT HUB — PLATFORM IMPACT REPORT'],
      [`Period: ${period}`],
      [`Generated: ${new Date().toLocaleString('en-GH')}`],
      [],
      ['OVERVIEW', ''],
      ['Total Registered Clients',     impact.overview.totalClients],
      ['Total Approved Experts',        impact.overview.totalExperts],
      ['Active Clients This Month',     impact.overview.activeClientsThisMonth],
      ['Total Sessions Held',           impact.overview.totalSessionsHeld],
      ['Total Canvas Submissions',      impact.overview.totalSubmissions],
      ['Graded Submissions',            impact.overview.gradedSubmissions],
      ['Level Completions',             impact.overview.levelCompletions],
      ['Pass Rate (%)',                  impact.overview.passRate],
      [],
      ['FINANCIALS (GHS)', ''],
      ['Total Gross Payable to Experts', impact.financials.totalGrossPayable],
      ['Platform Commission',            impact.financials.totalCommission],
      ['Total Net to Experts',           impact.financials.totalNetToExperts],
      [],
      ['SESSIONS BY TYPE', ''],
      ...impact.sessionsByType.map(s => [s.type, s.count]),
      [],
      ['LEVEL COMPLETIONS', ''],
      ...impact.levelBreakdown.map(l => [l.levelName, l.completions]),
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(impactRows);
    ws2['!cols'] = [{ wch: 35 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Platform Impact');
  }

  XLSX.writeFile(wb, `TourEntHub_Report_${period.replace(/\s/g, '_')}.xlsx`);
  toast.success('Excel report downloaded');
};

// ── Print / PDF ───────────────────────────────────────────────
const printReport = () => { window.print(); };

// ── Main Page ─────────────────────────────────────────────────
const AdminReportsPage = () => {
  const [year,         setYear]        = useState(currentYear);
  const [month,        setMonth]       = useState(currentMonth);
  // const [activeTab,    setActiveTab]   = useState('payments');
  const [activeTab,    setActiveTab]   = useState('rates');
  const [rateModal,    setRateModal]   = useState(null); // { id, name, rate }
  const [refetchKey,   setRefetchKey]  = useState(0);
  const [downloadingTimesheet, setDownloadingTimesheet] = useState(null); // expertProfileId being downloaded

  const period = `${MONTHS[month]} ${year}`;

  const { data: earningsData, loading: earningsLoading, error: earningsError } = useApi(
    () => adminService.getEarningsSummary({ year, month }),
    [year, month, refetchKey],
  );
  const { data: impactData, loading: impactLoading, error: impactError } = useApi(
    () => adminService.getImpactReport({ year, month }),
    [year, month, refetchKey],
  );
  const { data: expertsData } = useApi(() => adminService.getAllExperts({ limit: 100 }), []);
  const { data: runsData, loading: runsLoading } = useApi(() => adminService.getPaymentRuns(), [refetchKey]);

  const summary  = earningsData?.data?.summary ?? earningsData?.summary ?? [];
  const impact   = impactData?.data?.report    ?? impactData?.report    ?? null;
  const experts  = (Array.isArray(expertsData) ? expertsData : Array.isArray(expertsData?.experts) ? expertsData.experts : Array.isArray(expertsData?.data) ? expertsData.data : []).map(e => ({
    id:    e.id,
    name:  `${e.user?.firstName ?? ''} ${e.user?.lastName ?? ''}`.trim(),
    email: e.user?.email,
    rate:  e.paymentRate ?? null,
  }));
  const runs = runsData?.data?.runs ?? runsData?.runs ?? [];

  const handleDownloadTimesheet = async (expert) => {
    setDownloadingTimesheet(expert.id);
    try {
      const res = await adminService.downloadTimesheet(expert.id, { year, month });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `Timesheet-${expert.name.replace(/\s+/g, '-')}-${MONTHS[month]}-${year}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Timesheet downloaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not generate timesheet');
    } finally {
      setDownloadingTimesheet(null);
    }
  };

  const handleCreateRun = async () => {
    if (!window.confirm(`Create payment run for ${period}? This will batch all pending earnings.`)) return;
    try {
      await adminService.createPaymentRun({ year, month });
      toast.success('Payment run created');
      setRefetchKey(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create payment run');
    }
  };

  const handleApproveRun = async (runId) => {
    const ref = window.prompt('Enter MasterCard/funder reference number (optional):') ?? '';
    try {
      await adminService.approvePaymentRun(runId, { reference: ref });
      toast.success('Payment run marked as paid');
      setRefetchKey(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not approve run');
    }
  };

  return (
    <DashboardLayout title="Reports & Payments">
      {rateModal && (
        <RateModal
          expert={rateModal}
          existingRate={rateModal.rate}
          onSaved={() => setRefetchKey(k => k + 1)}
          onClose={() => setRateModal(null)}
        />
      )}

      <div className="max-w-6xl space-y-6 print:space-y-4">

        {/* Period selector + export */}
        <div className="flex items-center gap-3 flex-wrap">
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
            className="px-3 py-2 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white">
            {MONTHS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            className="px-3 py-2 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white">
            {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setRefetchKey(k => k + 1)}
            className="p-2 text-forest-500 hover:text-forest-800 border border-cream-200 rounded-xl hover:bg-cream-50 transition-colors">
            <RefreshCw size={15} />
          </button>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => exportToExcel(summary, impact, period)}
              disabled={earningsLoading || !summary.length}
              className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-full border border-forest-200 text-forest-700 hover:bg-forest-50 transition-colors disabled:opacity-40">
              <Download size={14} /> Export Excel
            </button>
            <button onClick={printReport}
              className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-full border border-forest-200 text-forest-700 hover:bg-forest-50 transition-colors print:hidden">
              <FileText size={14} /> Export PDF
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-cream-200 print:hidden">
          {[
            // { id: 'payments', label: 'Expert Payments' },
            // { id: 'impact',   label: 'Platform Impact' },
            { id: 'rates',    label: 'Payment Rates' },
            // { id: 'runs',     label: 'Payment Runs' },
            { id: 'timesheets', label: 'Timesheets' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`text-sm font-medium px-4 py-3 border-b-2 transition-colors ${
                activeTab === t.id
                  ? 'border-forest-600 text-forest-900'
                  : 'border-transparent text-forest-500 hover:text-forest-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Expert Payments ── */}
        {activeTab === 'payments' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl text-forest-900">Expert Payment Summary — {period}</h2>
              <button onClick={handleCreateRun}
                className="btn-primary text-sm flex items-center gap-2">
                <DollarSign size={14} /> Create Payment Run
              </button>
            </div>

            {earningsError && (
              <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
                <AlertCircle size={16} /> <span className="text-sm">Could not load earnings data.</span>
              </div>
            )}

            {earningsLoading ? (
              <div className="bg-white border border-cream-200 rounded-2xl p-8 animate-pulse space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-12 bg-cream-100 rounded-xl" />)}
              </div>
            ) : summary.length === 0 ? (
              <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
                <BarChart2 size={36} className="mx-auto mb-3 opacity-30" />
                <p className="font-serif text-xl text-forest-600">No earnings recorded</p>
                <p className="text-sm mt-2">Earnings are auto-logged when experts complete sessions or grade submissions.</p>
              </div>
            ) : (
              <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-cream-50 border-b border-cream-200">
                    <tr>
                      {['Expert', 'Sessions', 'Hours', 'Canvas Reviews', 'Gross (GHS)', 'Commission', 'Net Payable', 'Status'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-forest-600 uppercase tracking-widest">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cream-100">
                    {summary.map((e, i) => (
                      <tr key={i} className="hover:bg-cream-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-forest-900">{e.expertName}</div>
                          <div className="text-xs text-forest-400">{e.email}</div>
                        </td>
                        <td className="px-4 py-3 text-forest-700">{e.sessions}</td>
                        <td className="px-4 py-3 text-forest-700">{Number(e.sessionHours).toFixed(1)}h</td>
                        <td className="px-4 py-3 text-forest-700">{e.canvasReviews}</td>
                        <td className="px-4 py-3 font-medium text-forest-900">
                          {Number(e.totalGross).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-rust-500">
                          -{Number(e.totalCommission).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 font-bold text-green-700">
                          {Number(e.totalNet).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-semibold border px-2 py-0.5 rounded-full ${
                            e.status === 'PAID'     ? 'bg-green-50 text-green-700 border-green-200'
                            : e.status === 'APPROVED' ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>{e.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-forest-900">
                    <tr>
                      <td className="px-4 py-3 font-semibold text-cream-50 text-xs uppercase tracking-widest">TOTAL</td>
                      <td className="px-4 py-3 font-bold text-cream-50">{summary.reduce((s,e)=>s+e.sessions,0)}</td>
                      <td className="px-4 py-3 font-bold text-cream-50">{summary.reduce((s,e)=>s+Number(e.sessionHours),0).toFixed(1)}h</td>
                      <td className="px-4 py-3 font-bold text-cream-50">{summary.reduce((s,e)=>s+e.canvasReviews,0)}</td>
                      <td className="px-4 py-3 font-bold text-cream-50">GHS {summary.reduce((s,e)=>s+Number(e.totalGross),0).toFixed(2)}</td>
                      <td className="px-4 py-3 font-bold text-rust-300">-{summary.reduce((s,e)=>s+Number(e.totalCommission),0).toFixed(2)}</td>
                      <td className="px-4 py-3 font-bold text-green-300">GHS {summary.reduce((s,e)=>s+Number(e.totalNet),0).toFixed(2)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Platform Impact ── */}
        {activeTab === 'impact' && (
          <div className="space-y-6">
            <h2 className="font-serif text-xl text-forest-900">Platform Impact — {period}</h2>
            <p className="text-xs text-forest-500">This report can be submitted to MasterCard or any funding agency as evidence of platform activity and economic impact.</p>

            {impactLoading ? (
              <div className="grid sm:grid-cols-4 gap-4 animate-pulse">
                {[1,2,3,4].map(i => <div key={i} className="h-28 bg-white border border-cream-200 rounded-2xl" />)}
              </div>
            ) : impact ? (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard label="Registered Clients"     value={impact.overview.totalClients}             icon={Users}      />
                  <StatCard label="Approved Experts"       value={impact.overview.totalExperts}             icon={Star}       />
                  <StatCard label="Sessions Completed"     value={impact.overview.totalSessionsHeld}        icon={TrendingUp} />
                  <StatCard label="Level Completions"      value={impact.overview.levelCompletions}         icon={CheckCircle2} />
                  <StatCard label="Active Clients (30d)"   value={impact.overview.activeClientsThisMonth}   icon={Users}      />
                  <StatCard label="Canvas Submissions"     value={impact.overview.totalSubmissions}         icon={FileText}   />
                  <StatCard label="Pass Rate"              value={`${impact.overview.passRate}%`}           icon={CheckCircle2} />
                  <StatCard label="Net to Experts (GHS)"   value={`GHS ${Number(impact.financials.totalNetToExperts).toFixed(0)}`} icon={DollarSign} />
                </div>

                {/* Sessions by type */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="bg-white border border-cream-200 rounded-2xl p-5">
                    <h3 className="font-serif font-semibold text-forest-900 mb-4">Sessions by Type</h3>
                    <div className="space-y-3">
                      {impact.sessionsByType.map((s, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-sm font-medium text-forest-700 w-20">{s.type}</span>
                          <div className="flex-1 h-3 bg-cream-100 rounded-full overflow-hidden">
                            <div className="h-full bg-forest-600 rounded-full"
                              style={{ width: `${impact.overview.totalSessionsHeld > 0 ? (s.count / impact.overview.totalSessionsHeld) * 100 : 0}%` }} />
                          </div>
                          <span className="text-sm font-bold text-forest-900 w-8 text-right">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white border border-cream-200 rounded-2xl p-5">
                    <h3 className="font-serif font-semibold text-forest-900 mb-4">Level Completions</h3>
                    <div className="space-y-3">
                      {impact.levelBreakdown.map((l, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs font-medium text-forest-600 flex-1 truncate">{l.levelName}</span>
                          <span className="text-sm font-bold text-forest-900">{l.completions}</span>
                        </div>
                      ))}
                      {impact.levelBreakdown.length === 0 && (
                        <p className="text-sm text-forest-400">No completions yet</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Financial summary for funders */}
                <div className="bg-forest-900 rounded-2xl p-6">
                  <h3 className="font-serif font-semibold text-cream-50 mb-4">Financial Summary — {period}</h3>
                  <div className="grid sm:grid-cols-3 gap-6">
                    <div>
                      <div className="text-xs text-forest-400 uppercase tracking-widest mb-1">Total Gross Payable</div>
                      <div className="font-serif text-2xl font-bold text-cream-50">GHS {Number(impact.financials.totalGrossPayable).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-forest-400 uppercase tracking-widest mb-1">Platform Revenue</div>
                      <div className="font-serif text-2xl font-bold text-gold-400">GHS {Number(impact.financials.totalCommission).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-forest-400 uppercase tracking-widest mb-1">Net to Experts</div>
                      <div className="font-serif text-2xl font-bold text-green-400">GHS {Number(impact.financials.totalNetToExperts).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
                <BarChart2 size={36} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No impact data available</p>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Payment Rates ── */}
        {activeTab === 'rates' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl text-forest-900">Expert Payment Rates</h2>
              <p className="text-xs text-forest-400">Set rates per expert. Earnings are auto-calculated when sessions/reviews are completed.</p>
            </div>
            <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-cream-50 border-b border-cream-200">
                  <tr>
                    {['Expert', 'Session Rate/hr', 'Canvas Review Rate', 'Commission %', 'Currency', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-forest-600 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-100">
                  {experts.map((e, i) => (
                    <tr key={i} className="hover:bg-cream-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-forest-900">{e.name}</div>
                        <div className="text-xs text-forest-400">{e.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {e.rate ? <span className="font-medium text-forest-900">{e.rate.currency} {Number(e.rate.sessionRatePerHour).toFixed(2)}</span>
                          : <span className="text-forest-300 italic text-xs">Not set</span>}
                      </td>
                      <td className="px-4 py-3">
                        {e.rate ? <span className="font-medium text-forest-900">{e.rate.currency} {Number(e.rate.canvasReviewRate).toFixed(2)}</span>
                          : <span className="text-forest-300 italic text-xs">Not set</span>}
                      </td>
                      <td className="px-4 py-3">
                        {e.rate ? `${e.rate.platformCommission}%` : '—'}
                      </td>
                      <td className="px-4 py-3">{e.rate?.currency ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => setRateModal(e)}
                            className="flex items-center gap-1.5 text-xs font-medium text-forest-600 border border-forest-200 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors">
                            <Edit2 size={11} /> {e.rate ? 'Edit Rate' : 'Set Rate'}
                          </button>
                          <button onClick={() => handleDownloadTimesheet(e)}
                            disabled={downloadingTimesheet === e.id}
                            className="flex items-center gap-1.5 text-xs font-medium text-forest-600 border border-forest-200 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
                            {downloadingTimesheet === e.id
                              ? <><Loader2 size={11} className="animate-spin" /> Generating...</>
                              : <><Download size={11} /> Timesheet</>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Tab: Payment Runs ── */}
        {activeTab === 'runs' && (
          <div className="space-y-5">
            <h2 className="font-serif text-xl text-forest-900">Payment Runs</h2>
            <p className="text-xs text-forest-400">
              A payment run batches all pending earnings for a period. Once approved it can be submitted to MasterCard or your funder as a payment request with a reference number.
            </p>

            {runsLoading ? (
              <div className="space-y-3">
                {[1,2].map(i => <div key={i} className="h-20 bg-white border border-cream-200 rounded-2xl animate-pulse" />)}
              </div>
            ) : runs.length === 0 ? (
              <div className="text-center py-12 bg-white border border-cream-200 rounded-2xl text-forest-400">
                <Clock size={32} className="mx-auto mb-3 opacity-30" />
                <p className="font-serif text-lg text-forest-600">No payment runs yet</p>
                <p className="text-sm mt-2">Go to Expert Payments tab and click "Create Payment Run" for a period.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {runs.map((run, i) => (
                  <div key={i} className={`bg-white border-2 rounded-2xl p-5 flex items-center gap-5 ${
                    run.status === 'PAID' ? 'border-green-200' : run.status === 'APPROVED' ? 'border-blue-200' : 'border-amber-200'
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-serif font-semibold text-forest-900">{MONTHS[run.periodMonth]} {run.periodYear}</h4>
                        <span className={`text-[10px] font-semibold border px-2 py-0.5 rounded-full ${
                          run.status === 'PAID'     ? 'bg-green-50 text-green-700 border-green-200'
                          : run.status === 'APPROVED' ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>{run.status}</span>
                        {run.reference && (
                          <span className="text-xs text-forest-400">Ref: {run.reference}</span>
                        )}
                      </div>
                      <div className="flex gap-5 text-xs text-forest-500">
                        <span>{run.totalExperts} experts</span>
                        <span>Gross: GHS {Number(run.totalGrossAmount).toFixed(2)}</span>
                        <span>Commission: GHS {Number(run.totalCommission).toFixed(2)}</span>
                        <span className="font-semibold text-green-700">Net: GHS {Number(run.totalNetAmount).toFixed(2)}</span>
                      </div>
                      {run.notes && <p className="text-xs text-forest-400 mt-1 italic">{run.notes}</p>}
                    </div>
                    {run.status !== 'PAID' && (
                      <button onClick={() => handleApproveRun(run.id)}
                        className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 flex-shrink-0">
                        <CheckCircle2 size={13} /> Mark as Paid
                      </button>
                    )}
                    {run.status === 'PAID' && run.paidAt && (
                      <div className="text-xs text-green-600 flex-shrink-0 flex items-center gap-1.5">
                        <CheckCircle2 size={14} />
                        Paid {new Date(run.paidAt).toLocaleDateString('en-GH')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { font-size: 12px; }
        }
      `}</style>

      {/* ── Tab: Timesheets ── */}
      {activeTab === 'timesheets' && (
        <AdminTimesheetsTab
          experts={experts}
          year={year}
          month={month}
          MONTHS={MONTHS}
          downloadingTimesheet={downloadingTimesheet}
          handleDownloadTimesheet={handleDownloadTimesheet}
          adminService={adminService}
        />
      )}
    </DashboardLayout>
  );
};

// ── Admin Timesheets Tab ──────────────────────────────────────
const AdminTimesheetsTab = ({ experts, year, month, MONTHS, downloadingTimesheet, handleDownloadTimesheet, adminService }) => {
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [timesheet,      setTimesheet]      = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);

  const loadTimesheet = async (expert) => {
    setSelectedExpert(expert);
    setLoading(true); setError(null); setTimesheet(null);
    try {
      const res  = await adminService.getTimesheetData(expert.id, { year, month });
      const body = res.data?.data ?? res.data;
      setTimesheet(body?.timesheet ?? body);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load timesheet');
    } finally { setLoading(false); }
  };

  const sessions = timesheet?.sessions ?? [];
  const summary  = timesheet?.summary  ?? {};

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-xl text-forest-900">Timesheets — {MONTHS[month]} {year}</h2>
          <p className="text-xs text-forest-400 mt-0.5">Select an expert to view and download their session timesheet.</p>
        </div>
        {selectedExpert && (
          <button onClick={() => handleDownloadTimesheet(selectedExpert)}
            disabled={downloadingTimesheet === selectedExpert.id || sessions.length === 0}
            className="flex items-center gap-2 text-sm font-medium bg-forest-800 text-cream-50 px-5 py-2.5 rounded-full hover:bg-forest-700 transition-colors disabled:opacity-50">
            {downloadingTimesheet === selectedExpert.id
              ? <><Loader2 size={14} className="animate-spin" /> Generating...</>
              : <><Download size={14} /> Download .docx</>}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Expert list */}
        <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 bg-cream-50 border-b border-cream-100">
            <p className="text-xs font-semibold text-forest-500 uppercase tracking-wider">Experts</p>
          </div>
          {experts.length === 0 ? (
            <p className="text-sm text-forest-400 p-4">No experts found.</p>
          ) : (
            <div className="divide-y divide-cream-50 max-h-96 overflow-y-auto">
              {experts.map(e => (
                <button key={e.id} onClick={() => loadTimesheet(e)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-cream-50 transition-colors ${selectedExpert?.id === e.id ? 'bg-forest-50' : ''}`}>
                  <div className="w-8 h-8 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 text-xs font-bold flex-shrink-0">
                    {e.name?.[0] ?? '?'}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${selectedExpert?.id === e.id ? 'text-forest-900' : 'text-forest-700'}`}>{e.name}</p>
                    <p className="text-[10px] text-forest-400 truncate">{e.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Timesheet detail */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedExpert && (
            <div className="bg-white border border-cream-200 rounded-2xl p-12 text-center">
              <FileText size={32} className="text-forest-200 mx-auto mb-3" />
              <p className="text-forest-400 text-sm">Select an expert to view their timesheet</p>
            </div>
          )}

          {selectedExpert && loading && (
            <div className="bg-white border border-cream-200 rounded-2xl p-8 flex items-center justify-center gap-2 text-forest-500 text-sm">
              <Loader2 size={16} className="animate-spin" /> Loading timesheet...
            </div>
          )}

          {selectedExpert && error && (
            <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
              <AlertCircle size={15} /> {error}
            </div>
          )}

          {selectedExpert && timesheet && !loading && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Sessions',    value: summary.totalSessions ?? 0 },
                  { label: 'Total Hours', value: summary.totalHours ?? '0.00' },
                  { label: 'Expert',      value: selectedExpert.name },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
                    <p className="font-serif text-2xl font-bold text-forest-900 truncate">{s.value}</p>
                    <p className="text-[10px] text-forest-400 uppercase tracking-wider mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Session rows */}
              <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-cream-50 border-b border-cream-100 text-[10px] font-semibold text-forest-500 uppercase tracking-widest">
                  <div className="col-span-3">Date</div>
                  <div className="col-span-2">Time</div>
                  <div className="col-span-2">Hours</div>
                  <div className="col-span-3">Client</div>
                  <div className="col-span-2">Type</div>
                </div>
                {sessions.length === 0 ? (
                  <p className="text-sm text-forest-400 p-6 text-center">No sessions for this period.</p>
                ) : sessions.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-cream-50 last:border-0 hover:bg-cream-50/50 transition-colors">
                    <div className="col-span-3 text-sm text-forest-700">{s.date}</div>
                    <div className="col-span-2">
                      <p className="text-xs text-forest-600">{s.startTime}</p>
                      <p className="text-[10px] text-forest-400">→ {s.endTime}</p>
                    </div>
                    <div className="col-span-2 text-sm text-forest-700">{s.durationHours}h</div>
                    <div className="col-span-3">
                      <p className="text-sm text-forest-700 truncate">{s.clientName}</p>
                      <p className="text-[10px] text-forest-400 truncate">{s.levelName}</p>
                    </div>
                    <div className="col-span-2 text-[10px] text-forest-500">{s.sessionType}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReportsPage;